/* eslint-disable camelcase */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useMemo, useRef, useState, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Filters, createFieldDefs } from '@workboard/ui-list-filters';
import {
  Popover, ThemeProvider, createTheme, popoverClasses,
} from '@mui/material';
import { mergeThemeOptions, themeOptions } from '@workboard/wobo-material';
import GoalServices from '../../../services/goal.services';
import KrTagService from '../../../krTags/services/krTag.service';
import { directDotedActions } from '../../../actions/directDoted.actions';
import { filteredOKR } from '../../../actions/okr/okrDetails.actions';
import { okrDetailConstants } from '../../../constants/okr/okrDetails.constants';
import { krdetailConstants } from '../../../constants/krdetails.constants';
import { reactLoader, removeReactLoader } from '../../../utils/common';
import {
  useCustomFields,
  mapCustomFieldToFieldDef,
  mapCustomFieldsToParams,
  buildCustomFieldInitialCriteria,
} from './useCustomFields';
import {
  applyHideEmptyOkrsToApiParams,
  buildFilterDataSet,
  clearOkrFiltersFromUrl,
  getFilterParamsFromUrl,
  getOkrFilterCount,
  isKrStatusFilterEnabled,
  isUiFiltersAppliedInUrl,
  urlHasOkrFilters,
} from '../../../utils/okrFilterParams.util';
import {
  buildTimePeriodInitialCriterion,
  getTimePeriodFieldDef,
  mapTimePeriodCriterionToParams,
} from '../../../utils/timePeriodFilter.util';
import { getRagOptions } from '../../../utils/metricProgressColor';

// =============================================================================
// Helpers: URL and Team ID Extraction
// =============================================================================

/**
 * Extracts the team ID from the current URL query parameters.
 * Handles various URL patterns:
 * - /wb/activity/workstreams?teamId=1&teamView
 * - /wb/goal/teamGoal?teamId=195348&filterMap=1
 * - /wb/goal/myGoals (no teamId - returns null)
 *
 * @returns {string|null} The team ID if found, null otherwise
 */
const getTeamIdFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    const teamId = url.searchParams.get('teamId');
    return teamId || null;
  } catch (err) {
    console.error('Failed to parse URL for teamId:', err);
    return null;
  }
};

// =============================================================================
// Custom Hook: usePaginationLink
// =============================================================================

/**
 * Hook to expose component props to OkrComp for infinite scroll pagination.
 * Sets window.ObjectiveFilter once on mount with a returnFilterParam method
 * that always returns fresh props via a ref.
 *
 * @param {Object} props - Component props to expose
 */
const usePaginationLink = (props) => {
  const propsRef = useRef(props);

  // Keep ref up to date with latest props (safe to mutate ref during render)
  propsRef.current = props;

  // Set window.ObjectiveFilter once on mount, use ref for fresh props
  useEffect(() => {
    window.ObjectiveFilter = {
      returnFilterParam: () => propsRef.current,
    };
    return () => {
      window.ObjectiveFilter = null;
    };
  }, []); // Empty deps - only run on mount/unmount
};

const StyledPopover = styled(Popover)({
  [`.${popoverClasses.paper}`]: {
    borderRadius: '8px',
    padding: '4px 16px 16px',
    marginTop: '5px',
    width: '650px',
    maxHeight: 'calc(100vh - 120px)', // in case the height is larger than the viewport it won't block the button
  },
});

// =============================================================================
// Helpers
// =============================================================================

/**
 * Maps Filters component status values to API params shape
 */
const mapStatusToParams = (filterValues) => {
  const criteria = filterValues?.group?.criteria || [];
  const statusCriteria = criteria.find((c) => c.fieldName === 'status');
  const values = statusCriteria?.values || [];

  // API expects comma-separated string: '1' (open), '2' (closed), or '1,2' (both)
  const status = values.map((v) => v.value).join(',') || String(okrDetailConstants.GOAL_ACTIVE);

  return {
    status,
  };
};

const getKrStatusOptions = (t) => [
  { label: t('Open'), value: String(krdetailConstants.METRIC_ACTIVE) },
  { label: t('Closed'), value: String(krdetailConstants.METRIC_CLOSED) },
];

/**
 * Maps Filters component Key Result Status values to the API metricStatus param ('1', '2' or '1,2')
 */
const mapKeyResultStatusToParams = (filterValues) => {
  const criteria = filterValues?.group?.criteria || [];
  const krStatusCriteria = criteria.find((c) => c.fieldName === 'keyResultStatus');
  const values = krStatusCriteria?.values || [];
  const metricStatus = values.map((v) => v.value).join(',') || String(krdetailConstants.METRIC_ACTIVE);

  return { metricStatus };
};

const buildKeyResultStatusCriterion = (metricStatus, t) => {
  const savedValues = String(metricStatus || krdetailConstants.METRIC_ACTIVE).split(',');
  return {
    fieldName: 'keyResultStatus',
    fieldType: 'multi-select-button',
    operator: 'is',
    values: getKrStatusOptions(t).filter((option) => savedValues.includes(option.value)),
    nonRemovable: true,
  };
};

/**
 * Maps Filters component category value to API params shape
 */
const mapCategoryToParams = (filterValues) => {
  const criteria = filterValues?.group?.criteria || [];
  const categoryCriteria = criteria.find((c) => c.fieldName === 'category');
  const values = categoryCriteria?.value;

  // API expects single category id (old component used selectedCategoryId)
  // Treat '0' or 'NONE' as empty (no category selected)
  const rawValue = values?.value ?? '';
  const categories = (rawValue === '0' || rawValue === 0) ? '' : rawValue;

  return { categories };
};

/**
 * Maps Filters component keyResultTags value to API params shape
 */
const mapKeyResultTagsToParams = (filterValues) => {
  const criteria = filterValues?.group?.criteria || [];
  const keyResultTagsCriteria = criteria.find((c) => c.fieldName === 'keyResultTags');
  const values = keyResultTagsCriteria?.values || [];
  const customTagId = values.map((v) => v.value).join(',') || '';

  return {
    // for persistence in Redux store (display selected Options in the UI)
    krTags: values?.map((v) => ({ id: v.value, name: v.label })),
    custom_tag_id: customTagId, // for filtering in API (API expects comma-separated string)
  };
};

/**
 * Maps Filters component performance (RAG Status) values to API params shape
 * API expects: performance=4,3,2,1,0 (comma-separated progress_color values)
 */
const mapPerformanceToParams = (filterValues) => {
  const criteria = filterValues?.group?.criteria || [];
  const performanceCriteria = criteria.find((c) => c.fieldName === 'performance');
  const values = performanceCriteria?.values || [];
  const performance = values.map((v) => v.value).join(',')
    || okrDetailConstants.FILTER_PROGRESS_DEFAULT + ',0';

  return { performance };
};

/**
 * Extracts goals array from API response
 */
const extractGoals = (response) => (
  response.data?.data?.goals || response.data?.goals || []
);

/**
 * Direct & Dotted tab index for deeplink URLs (Members=0, OKRs=1, Key Results=2).
 */
const getDirectDotedActiveTab = (pageType) => {
  if (pageType === okrDetailConstants.PAGE_TYPE_MY_DIRECT_OKR) {
    return 1;
  }
  if (pageType === okrDetailConstants.PAGE_TYPE_MY_DIRECT_KR) {
    return 2;
  }
  return undefined;
};

/**
 * Direct & Dotted OKRs tab — objective filter Apply/Reset.
 *
 * Cannot use the generic handleSubmit tail (goalServices[submitUrl] + filteredOKR):
 * - List data must go through GET_DIRECT_DOTTED_OKRS_SUCCESS, not FILTERED_DATA.
 * - getDirectDotedOkrs adds fetch-generation guarding, commentsData, and filter meta
 *   (clientFilterApplied, uiFiltersApplied, filterCount) that filteredOKR omits.
 * - Initial load / overdue / scroll already use OkrComp → getDirectDotedOkrs; filter apply
 *   must hit the same action so Redux and the backend stay in sync.
 */
const applyDirectDotedOkrObjectiveFilters = ({
  dispatch,
  paramsList,
  payload,
  isReset,
  dueStatus,
  onSuccess,
  onError,
}) => {
  const requestParams = {
    ...paramsList,
    ...payload,
    krTags: undefined,
  };
  if (isReset === true) {
    delete requestParams[okrDetailConstants.UI_FILTERS_APPLIED];
  }
  return dispatch(directDotedActions.getDirectDotedOkrs(
    requestParams,
    {
      dueStatus: dueStatus === true,
      filterCount: isReset ? 0 : undefined,
      clientFilterApplied: isReset ? false : undefined,
      uiFiltersApplied: isReset !== true,
    },
  ))
    .then(onSuccess)
    .catch(onError);
};

/**
 * Direct & Dotted Key Results tab — objective filter Apply/Reset.
 *
 * Same Redux contract issue as OKRs, plus a response-shape mismatch on the generic path:
 * - getMyDirectsKR returns data.metrics, but extractGoals() reads data.goals → [].
 * - filteredOKR would set teams to [], while the KR list expects teams.metrics.
 * - Success must dispatch GET_DIRECT_DOTTED_KRS_SUCCESS with filter meta, not FILTERED_DATA.
 */
/**
 * Team OKRs tab — objective filter Apply/Reset.
 *
 * Uses getTeamGoalData (same endpoint as initial load) and FILTERED_DATA so
 * showGoalMetricList is preserved. A follow-up getTeamGoalData must not reset
 * collapse via UPDATE_INITIAL_TEAMS_DATA.
 */
const applyTeamTabOkrObjectiveFilters = ({
  goalService,
  dispatch,
  paramsList,
  payload,
  isReset,
  dueStatus,
  onSuccess,
  onError,
}) => {
  const requestParams = {
    ...paramsList,
    ...payload,
    krTags: undefined,
    teamPage: true,
    jsonRes: 1,
  };
  if (isReset === true) {
    delete requestParams[okrDetailConstants.UI_FILTERS_APPLIED];
  }
  applyHideEmptyOkrsToApiParams(requestParams, {
    clientFilterApplied: isReset !== true,
    dueStatus: dueStatus === true,
    appliedListFilters: isReset !== true,
  });
  return goalService.getTeamGoalData(requestParams)
    .then((response) => {
      const goals = extractGoals(response);
      dispatch(filteredOKR(
        goals,
        buildFilterDataSet(payload),
        isReset ? 0 : getCount(payload),
      ));
      onSuccess();
    })
    .catch(onError);
};

const applyDirectDotedKrObjectiveFilters = ({
  dispatch,
  goalService,
  paramsList,
  payload,
  isReset,
  dueStatus,
  showOption,
  onSuccess,
  onError,
}) => {
  const requestParams = {
    ...paramsList,
    ...payload,
    flag: 2,
    kr_own_update: paramsList?.kr_own_update ?? showOption,
    krTags: undefined,
  };
  if (isReset === true) {
    delete requestParams[okrDetailConstants.UI_FILTERS_APPLIED];
  }
  dispatch({ type: okrDetailConstants.RESET_GOAL_DATA });
  return goalService.getMyDirectsKR(requestParams)
    .then((response) => {
      if (response?.data) {
        dispatch({
          type: okrDetailConstants.GET_DIRECT_DOTTED_KRS_SUCCESS,
          data: response.data,
          meta: {
            fromFilter: true,
            filterDataSet: buildFilterDataSet(payload),
            filterCount: isReset ? 0 : getCount(payload),
            clientFilterApplied: isReset !== true,
            dueStatus: dueStatus === true,
          },
        });
      }
      onSuccess();
    })
    .catch(onError);
};

/**
 * Updates URL with filter params (like old component's setDeepLink)
 */
const updateUrlWithFilters = (params) => {
  const url = new URL(window.location.href);

  Object.entries(params).forEach(([key, value]) => {
    if (typeof value !== 'undefined') {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
  });

  window.history.replaceState({}, '', url.toString());
};

/**
 * Parses customFields payload from URL/store into array format
 * @param {string|Array|undefined|null} customFieldsValue
 * @returns {Array}
 */
const parseCustomFieldsPayload = (customFieldsValue) => {
  if (!customFieldsValue) {
    return [];
  }
  if (Array.isArray(customFieldsValue)) {
    return customFieldsValue;
  }
  if (typeof customFieldsValue === 'string') {
    try {
      const parsed = JSON.parse(customFieldsValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }
  return [];
};

/**
 * Reads custom fields filter values from URL
 * @returns {Array} Parsed custom fields array or empty array
 *
 * URL format: customFields=[{"fieldId":123,"value":"High","operator":"equals","objectType":5}]
 */
const getCustomFieldsFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    const customFieldsParam = url.searchParams.get('customFields');
    return parseCustomFieldsPayload(customFieldsParam);
  } catch (err) {
    console.error('Failed to parse customFields from URL:', err);
    return [];
  }
};

/**
 * Builds Filters initialValues from saved filter data
 *
 * @param {Object} params
 * @param {Object} params.filterData - Saved filter data
 * @param {Array} params.categoryList - Available categories
 * @param {Array} params.customFieldCriteria - Pre-built custom field criteria from URL
 * @param {Function} params.t - Translation function
 * @param {string} params.type - Page type (e.g., 'heatmap' to hide certain fields)
 */
const buildInitialValues = (params) => {
  const {
    filterData,
    categoryList = [],
    customFieldCriteria = [],
    t,
    type,
  } = params || {};
  // Convert MM/DD/YYYY to YYYY-MM-DD for the Filters component
  // Accepts both M/D/YYYY and MM/DD/YYYY formats from PHP
  const status = [
    filterData?.onlyOpenGoals ? { label: t('Open'), value: '1' } : null,
    filterData?.onlyClosedGoals ? { label: t('Closed'), value: '2' } : null,
  ].filter(Boolean);
  // Filter out 'NONE' (id: '0') category - treat as empty selection
  const categories = categoryList
    ?.filter((category) =>
      category.id !== '0' && category.id !== 0 && String(filterData?.categories) === String(category.id)
    )
    .map((category) => ({ label: category.name, value: category.id })) || [];
  const krTags = filterData?.krTags?.map((tag) => ({ label: tag.name, value: tag.id })) || [];
  const performance = filterData?.performance?.split(',')
    ?.map((val) => getRagOptions(t).find((option) => option.value === val))
    .filter(Boolean) || getRagOptions(t);
  // Static criteria (always present)
  // Key Result Tags is hidden for heatmap view
  const staticCriteria = [
    {
      fieldName: 'status',
      fieldType: 'multi-select-button',
      operator: 'is',
      values: status.length > 0 ? status : [{ label: t('Open'), value: '1' }],
      nonRemovable: true,
    },
    ...(isKrStatusFilterEnabled(type)
      ? [buildKeyResultStatusCriterion(filterData?.metricStatus, t)]
      : []),
    buildTimePeriodInitialCriterion({
      startDate: filterData?.startDate,
      endDate: filterData?.endDate,
      exDate: filterData?.exDate,
    }),
    {
      fieldName: 'category',
      fieldType: 'single-select',
      operator: 'is',
      value: categories[0] ?? null,
      nonRemovable: true,
    },
    ...(type !== 'heatmap' ? [{
      fieldName: 'keyResultTags',
      fieldType: 'multi-select',
      operator: 'includes',
      values: krTags,
      nonRemovable: true,
    }] : []),
    {
      fieldName: 'performance',
      fieldType: 'multi-select-button',
      operator: 'includes',
      values: performance,
      nonRemovable: true,
    },
  ];

  return {
    group: {
      operator: 'AND',
      criteria: [
        ...staticCriteria,
        ...customFieldCriteria,
      ],
    },
  };
};

const getCount = (payload) => getOkrFilterCount(payload);

// =============================================================================
// Field Definitions
// =============================================================================

// Theme overrides to prevent global styles from affecting Filters component
const filtersThemeExtensions = {
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: 'auto',
          minHeight: '40px',
        },
        input: {
          height: 'auto',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: 'auto',
        },
        input: {
          height: '24px',
          padding: '8.5px 14px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          height: 'auto',
          minHeight: '1.4375em',
        },
      },
    },
  },
};

const filtersTheme = createTheme(mergeThemeOptions(themeOptions, filtersThemeExtensions));

// =============================================================================
// Field Definitions Hook
// =============================================================================

/**
 * Hook to create field definitions for the Filters component
 * Combines static fields (timePeriod, status, category, keyResultTags)
 * with dynamic custom fields from the API
 *
 * @param {Object} params
 * @param {Array} params.categoryList - List of OKR categories
 * @param {Array} params.customFields - Custom fields from useCustomFields hook
 * @returns {Object} Field definitions for Filters component
 */
const useFieldDefs = (params) => {
  const { categoryList, customFields = [], type } = params || {};
  // Filter out 'NONE' category (id: '0') - it's a placeholder for empty value
  const categoryOptions = categoryList
    ?.filter((category) => category.id !== '0' && category.id !== 0)
    ?.map((category) => ({ label: category.name, value: category.id })) || [];
  const { t } = useTranslation();

  return useMemo(() => {
    // Static field definitions
    const staticFields = {
      timePeriod: getTimePeriodFieldDef(t),
      status: {
        label: t('Objective Status'),
        type: 'multi-select-button',
        operators: ['is'],
        options: () => [
          { label: t('Open'), value: '1' },
          { label: t('Closed'), value: '2' },
        ],
      },
      ...(isKrStatusFilterEnabled(type) && {
        keyResultStatus: {
          label: t('Key Result Status'),
          type: 'multi-select-button',
          operators: ['is'],
          options: () => getKrStatusOptions(t),
        },
      }),
      category: {
        label: t('Objective Category'),
        type: 'single-select',
        operators: ['is'],
        options: () => categoryOptions,
      },
      ...(type !== 'heatmap' && {
        keyResultTags: {
          label: t('Key Result Tags'),
          type: 'multi-select',
          operators: ['includes'],
          options: async (term) => {
            if (!term || term.length < 1) {
              return [];
            }
            const krTagService = new KrTagService();
            const response = await krTagService.searchKrTags({ term });
            const tags = response.data?.data?.tags || [];
            return tags.map((tag) => ({ label: tag.name, value: tag.id }));
          },
        },
      }),
      performance: {
        label: t('Objective RAG'),
        type: 'multi-select-button',
        operators: ['includes'],
        options: () => getRagOptions(t),
      },
    };

    // Dynamic custom field definitions (matches RBR naming: customField-{id})
    const customFieldDefs = customFields.reduce((acc, field) => {
      acc[`customField-${field.id}`] = mapCustomFieldToFieldDef(field);
      return acc;
    }, {});

    // Merge static and custom field definitions
    const fields = {
      ...staticFields,
      ...customFieldDefs,
    };

    return createFieldDefs({ fields });
  }, [t, categoryOptions, customFields, type]);
};

// =============================================================================
// Component
// =============================================================================

export const ObjectiveFilter = (props) => {
  const {
    open,
    close,
    submitUrl,
    paramsList,
    filterDataSet,
    filterSelectedData: initialFilterDataSet,
    updateOkrFilterVal,
    anchorEl,
    teams,
    count,
    categoryList,
    type,
    showOption,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dueStatus = useSelector((state) => state.okrDetailsReducer?.dueStatus === true);
  const clientFilterApplied = useSelector(
    (state) => state.okrDetailsReducer?.clientFilterApplied === true,
  );
  const goalServices = useRef(new GoalServices());
  const paginationLinkProps = { ...props, dueStatus };

  // Expose props to OkrComp for infinite scroll pagination
  usePaginationLink(paginationLinkProps);

  // Extract team ID from URL for custom field filtering
  // This ensures only team-relevant custom fields are shown in filters
  const urlTeamId = getTeamIdFromUrl();
  const teamIds = urlTeamId ? [urlTeamId] : [];

  // Fetch custom fields for Objectives, filtered by current team context
  const { customFields, customFieldIds, loading: customFieldsLoading } = useCustomFields({ teamIds });

  // Create field definitions with custom fields included
  const fieldDefs = useFieldDefs({ categoryList, customFields, type });

  // Read custom fields from URL and validate against available options
  const customFieldCriteria = useMemo(() => {
    if (!customFields.length) {
      return [];
    }
    const urlCustomFields = getCustomFieldsFromUrl();
    const stateCustomFields = parseCustomFieldsPayload(
      filterDataSet?.customFields ?? initialFilterDataSet?.customFields
    );
    const sourceCustomFields = urlCustomFields.length ? urlCustomFields : stateCustomFields;
    return buildCustomFieldInitialCriteria(sourceCustomFields, customFields);
  }, [customFields, filterDataSet, initialFilterDataSet]);

  const urlFilterDataSet = useMemo(() => {
    if (!urlHasOkrFilters() && !isUiFiltersAppliedInUrl()) {
      return null;
    }
    return buildFilterDataSet(getFilterParamsFromUrl());
  }, []);

  // Normalize to canonical format (buildFilterDataSet handles multiple input shapes)
  const normalizedFilterData = useMemo(() => {
    const sourceFilterData = clientFilterApplied
      ? (filterDataSet || initialFilterDataSet)
      : (filterDataSet || urlFilterDataSet || initialFilterDataSet);
    return buildFilterDataSet(sourceFilterData);
  }, [filterDataSet, initialFilterDataSet, urlFilterDataSet, clientFilterApplied]);
  const initialValues = useMemo(() =>
    buildInitialValues({
      filterData: normalizedFilterData,
      categoryList,
      customFieldCriteria,
      t,
      type,
    }),
  [normalizedFilterData, categoryList, customFieldCriteria, type]);

  const handleSubmit = (event = {}) => {
    const isReset = !event || (Object.keys(event).length === 0);
    const timePeriodParams = mapTimePeriodCriterionToParams(event?.values);
    const statusParams = mapStatusToParams(event?.values);
    const categoryParams = mapCategoryToParams(event?.values);
    const keyResultTagsParams = mapKeyResultTagsToParams(event?.values);
    const customFieldsParams = mapCustomFieldsToParams(event?.values, customFieldIds);
    const performanceParams = mapPerformanceToParams(event?.values);
    const keyResultStatusParams = isKrStatusFilterEnabled(type)
      ? mapKeyResultStatusToParams(event?.values)
      : {};

    const payload = {
      ...timePeriodParams,
      ...statusParams,
      ...keyResultStatusParams,
      ...categoryParams,
      ...keyResultTagsParams,
      ...customFieldsParams,
      ...performanceParams,
      offset: 0,
      jsonRes: 1,
      fromFilter: true,
    };
    if (dueStatus === true) {
      payload.overdueKR = 1;
    }
    if (isReset !== true) {
      payload[okrDetailConstants.UI_FILTERS_APPLIED] = 1;
    }
    applyHideEmptyOkrsToApiParams(payload, { dueStatus: dueStatus === true });
    reactLoader();

    const finishFilterApply = () => {
      if (typeof window.OkrComp !== 'undefined') {
        window.OkrComp.resetStateOffset();
      }
      updateOkrFilterVal?.(
        payload.startDate,
        payload.endDate,
        payload.status?.split(','),
        payload.performance,
        payload.categories,
        payload.exDate,
        payload.customFields,
      );
      if (isReset) {
        clearOkrFiltersFromUrl();
      } else {
        const directDotedActiveTab = getDirectDotedActiveTab(type);
        updateUrlWithFilters({
          ...payload,
          jsonRes: undefined,
          qt: undefined,
          krTags: undefined,
          overdueKR: dueStatus === true ? 1 : undefined,
          ...(directDotedActiveTab !== undefined ? { activeTab: directDotedActiveTab } : {}),
        });
      }
      close();
      removeReactLoader();
    };

    if (submitUrl === 'getMyDirectsOKR') {
      applyDirectDotedOkrObjectiveFilters({
        dispatch,
        paramsList,
        payload,
        isReset,
        dueStatus,
        onSuccess: finishFilterApply,
        onError: removeReactLoader,
      });
      return;
    }

    if (submitUrl === 'getMyDirectsKR') {
      applyDirectDotedKrObjectiveFilters({
        dispatch,
        goalService: goalServices.current,
        paramsList,
        payload,
        isReset,
        dueStatus,
        showOption,
        onSuccess: finishFilterApply,
        onError: removeReactLoader,
      });
      return;
    }

    if (type === okrDetailConstants.PAGE_TYPE_TEAM_TAB_OKR) {
      applyTeamTabOkrObjectiveFilters({
        goalService: goalServices.current,
        dispatch,
        paramsList,
        payload,
        isReset,
        dueStatus,
        onSuccess: finishFilterApply,
        onError: removeReactLoader,
      });
      return;
    }

    goalServices.current[submitUrl]({
      ...paramsList,
      ...payload,
      krTags: undefined,
    }).then((response) => {
      const goals = extractGoals(response);
      const newFilterDataSet = buildFilterDataSet(payload);
      const newCount = isReset ? 0 : getCount(payload);
      dispatch(filteredOKR(goals, newFilterDataSet, newCount));
      finishFilterApply();
    }).catch(() => {
      removeReactLoader();
    });
  };

  const handleReset = () => {
    // Reset pagination state for infinite scroll
    if (typeof window.OkrComp !== 'undefined') {
      window.OkrComp.resetStateOffset();
    }
    // submit with empty values
    handleSubmit();
  };

  const filtersKey = [
    customFieldsLoading ? 'cf-loading' : 'cf-loaded',
    normalizedFilterData.status,
    normalizedFilterData.onlyOpenGoals,
    normalizedFilterData.onlyClosedGoals,
    normalizedFilterData.metricStatus,
    normalizedFilterData.exDate,
    normalizedFilterData.startDate,
    normalizedFilterData.endDate,
  ].join('-');

  return (
    <ThemeProvider theme={filtersTheme}>
      <StyledPopover
        anchorEl={anchorEl}
        open={open}
        keepMounted
        onClose={() => close()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Filters
          key={filtersKey}
          fieldDefs={fieldDefs}
          initialValues={initialValues}
          isApplyDisabled={false}
          allowEmptyFiltersList
          onReset={handleReset}
          onSubmit={handleSubmit}
          onClose={() => close()}
          variant="compact"
        />
      </StyledPopover>
    </ThemeProvider>
  );
};

const mapStateToProps = (state) => ({
  ...state.okrDetailsReducer,
});

export default connect(mapStateToProps)(ObjectiveFilter);
